/**
 * ポイントショップ管理画面 - カスタマイズ対応版
 * スマホからの写真アップロードも可能
 */

import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, Save, Camera, Edit2, X, Image, Sparkles } from 'lucide-react';
import apiClient from '../../utils/apiClient';

const COUPON_DISCOUNT_VALUE_ERROR = 'クーポン交換の場合、値引き値は1円以上を設定してください。必要ポイントと値引き値は別項目です。';

const PointShopManagerCustom: React.FC = () => {
  const [prizes, setPrizes] = useState<any[]>([]);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [editingPrize, setEditingPrize] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewImage, setPreviewImage] = useState<string>('');
  const couponSettingsCacheRef = useRef<Record<number, { discount_type: string; discount_value: number }>>({});

  // カスタム景品の初期値
  const initCustomPrize = () => ({
    name: '',
    points: 100,
    icon: '🎁',
    desc: '',
    category: 'custom',
    discount_type: 'fixed',
    discount_value: 0,
    image: null
  });

  // アイコン選択肢（拡張版）
  const iconCategories = [
    {
      name: '食べ物',
      icons: ['🍱', '🍛', '🍜', '🍔', '🍟', '🍕', '🍗', '🥘', '🍖', '🥓', '🍳', '🥚']
    },
    {
      name: '飲み物',
      icons: ['☕', '🍵', '🥤', '🧃', '🥛', '🍺', '🍶', '🍷', '🥃', '🍹', '🧋', '🧉']
    },
    {
      name: 'デザート',
      icons: ['🍰', '🎂', '🧁', '🍪', '🍩', '🍮', '🍨', '🍧', '🍦', '🥧', '🍫', '🍬']
    },
    {
      name: 'グッズ',
      icons: ['👕', '👔', '🥢', '🍴', '🥄', '🏺', '🎁', '🎀', '🛍️', '📦', '💳', '🎫']
    },
    {
      name: '特別',
      icons: ['👑', '⭐', '🌟', '💎', '🏆', '🥇', '🎖️', '🏅', '🎯', '🎪', '🎭', '🎨']
    },
    {
      name: 'その他',
      icons: ['❤️', '💝', '🎉', '🎊', '✨', '🔥', '💯', '🆕', '🆓', '🎌', '🏮', '🧧']
    }
  ];

  const [pointSettings, setPointSettings] = useState({
    earnRate: 500
  });
  const [initialEarnRate, setInitialEarnRate] = useState<number | null>(null);

  const getItemType = (item: any) => item.item_type || item.category || 'custom';
  const couponItems = prizes.filter((item) => getItemType(item) === 'coupon');
  const prizeItems = prizes.filter((item) => getItemType(item) !== 'coupon');

  const renderPrizeCards = (items: any[]) => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map(prize => (
        <div key={prize.id} className="bg-gray-50 rounded-lg p-4 relative hover:shadow-lg transition-all">
          {/* 編集・削除ボタン */}
          <div className="absolute top-2 right-2 flex space-x-1">
            <button
              onClick={() => editPrize(prize)}
              className="w-8 h-8 bg-blue-500 text-white rounded-full hover:bg-blue-600"
            >
              <Edit2 className="w-4 h-4 mx-auto" />
            </button>
            <button
              onClick={() => deletePrize(prize.id)}
              className="w-8 h-8 bg-red-500 text-white rounded-full hover:bg-red-600"
            >
              ×
            </button>
          </div>

          {/* 画像またはアイコン */}
          {prize.image ? (
            <div className="w-full h-32 mb-3 rounded-lg overflow-hidden bg-gray-200">
              <img src={prize.image} alt={prize.name} className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="text-5xl mb-3 text-center">{prize.icon}</div>
          )}

          <div className="font-bold text-lg">{prize.name}</div>
          {prize.desc && (
            <div className="text-gray-600 text-sm mb-2">{prize.desc}</div>
          )}
          <div className="text-purple-600 font-bold text-xl">{prize.points}pt</div>
          <div className={`text-sm mt-2 ${prize.active ? 'text-green-600' : 'text-gray-400'}`}>
            {prize.active ? '● 公開中' : '● 非公開'}
          </div>
        </div>
      ))}
    </div>
  );

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // 景品一覧を取得
      const response = await apiClient.get('/shop/items');
      const items = response.data.items || response.data || [];
      setPrizes(items.map((p: any) => ({
        ...(couponSettingsCacheRef.current[Number(p.id)] || {}),
        ...p,
        points: p.points_required || p.points || 0,
        category: p.category || p.item_type || 'custom',
        discount_type: p.discount_type || couponSettingsCacheRef.current[Number(p.id)]?.discount_type || 'fixed',
        discount_value: p.discount_value ?? couponSettingsCacheRef.current[Number(p.id)]?.discount_value ?? 0,
        desc: p.description,
        image: p.image_url
      })));

      // 設定を取得
      const settingsResponse = await apiClient.get('/shop/settings');
      if (settingsResponse.data) {
        const loadedEarnRate = settingsResponse.data.earn_rate || 500;
        setPointSettings({
          earnRate: loadedEarnRate
        });
        setInitialEarnRate(loadedEarnRate);
      }
    } catch (error) {
      console.error('データ読み込みエラー:', error);
    }
  };

  // スマホカメラ・画像選択
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // 画像プレビュー
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result as string);
        if (editingPrize) {
          setEditingPrize({ ...editingPrize, image: reader.result });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // カスタム景品を追加
  const addCustomPrize = async () => {
    if (!editingPrize?.name || editingPrize.points <= 0) {
      alert('景品名とポイント数を入力してください');
      return;
    }

    const isCoupon = editingPrize.category === 'coupon';
    const discountType = editingPrize.discount_type === 'percentage' ? 'percentage' : 'fixed';
    const discountValue = Number(editingPrize.discount_value);

    if (isCoupon && (!Number.isFinite(discountValue) || discountValue <= 0)) {
      alert(COUPON_DISCOUNT_VALUE_ERROR);
      return;
    }

    try {
      if (editingPrize.id) {
        // 編集
        await apiClient.put(`/shop/items/${editingPrize.id}`, {
          name: editingPrize.name,
          description: editingPrize.desc,
          category: editingPrize.category || 'custom',
          points: editingPrize.points,
          icon: editingPrize.icon,
          image_url: editingPrize.image,
          discount_type: isCoupon ? discountType : null,
          discount_value: isCoupon ? discountValue : null,
          active: true
        });
        if (isCoupon) {
          couponSettingsCacheRef.current[Number(editingPrize.id)] = {
            discount_type: discountType,
            discount_value: discountValue
          };
        } else {
          delete couponSettingsCacheRef.current[Number(editingPrize.id)];
        }
      } else {
        // 新規追加
        const createResponse = await apiClient.post('/shop/items', {
          name: editingPrize.name,
          description: editingPrize.desc,
          category: editingPrize.category || 'custom',
          points: editingPrize.points,
          icon: editingPrize.icon,
          image_url: editingPrize.image,
          discount_type: isCoupon ? discountType : null,
          discount_value: isCoupon ? discountValue : null,
          active: true
        });
        if (isCoupon) {
          const createdId = Number(createResponse?.data?.id);
          if (Number.isFinite(createdId) && createdId > 0) {
            couponSettingsCacheRef.current[createdId] = {
              discount_type: discountType,
              discount_value: discountValue
            };
          }
        }
      }
      await loadData(); // リロード
    } catch (error) {
      console.error('保存エラー:', error);
      const msg = (error as any)?.response?.data?.error ?? (error as any)?.response?.data?.message ?? '保存に失敗しました';
      alert(msg);
      return;
    }

    setShowCustomForm(false);
    setEditingPrize(null);
    setPreviewImage('');
    showSuccessMessage();
  };

  // 景品を編集
  const editPrize = (prize: any) => {
    const category = prize.category || prize.item_type || 'custom';
    setEditingPrize({
      ...prize,
      category,
      discount_type: prize.discount_type || 'fixed',
      discount_value: prize.discount_value ?? 0
    });
    setPreviewImage(prize.image || '');
    setShowCustomForm(true);
  };

  // 景品を削除
  const deletePrize = async (id: number) => {
    if (!confirm('削除しますか？')) return;
    try {
      await apiClient.delete(`/shop/items/${id}`);
      await loadData();
    } catch (error) {
      console.error('削除エラー:', error);
      alert('削除に失敗しました');
    }
  };

  // 成功メッセージ表示
  const showSuccessMessage = () => {
    const element = document.getElementById('success-message');
    if (element) {
      element.classList.remove('hidden');
      setTimeout(() => element.classList.add('hidden'), 3000);
    }
  };

  const scrollToSection = (sectionId: string) => {
    const target = document.getElementById(sectionId);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // 全体保存
  const saveAll = async () => {
    try {
      const settingsPayload: Record<string, any> = {};
      // 「何円で1ポイント？」は /settings/points を正とするため、
      // この画面では編集が発生した場合のみ送信する（現状は未送信）。
      if (initialEarnRate !== null && pointSettings.earnRate !== initialEarnRate) {
        settingsPayload.earnRate = pointSettings.earnRate;
      }

      if (Object.keys(settingsPayload).length > 0) {
        await apiClient.post('/shop/settings', settingsPayload);
      }
      alert('✅ 設定を保存しました！');
    } catch (error) {
      console.error('設定保存エラー:', error);
      alert('設定の保存に失敗しました');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-pink-50 p-4 md:p-6">
      {/* ヘッダー */}
      <div className="max-w-6xl mx-auto mb-6">
        <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-4 text-center">
            <Sparkles className="inline-block mr-3 text-yellow-500" />
            ポイントショップ管理
            <Sparkles className="inline-block ml-3 text-yellow-500" />
          </h1>
          <p className="text-lg md:text-xl text-center text-gray-600">
            自由に景品を作成・カスタマイズできます
          </p>
        </div>
      </div>

      {/* 成功メッセージ */}
      <div id="success-message" className="hidden fixed top-4 right-4 bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg z-50 animate-bounce">
        ✅ 景品を追加しました！
      </div>

      {/* カスタム景品作成ボタン */}
      <div className="max-w-6xl mx-auto mb-6">
        <button
          onClick={() => {
            setEditingPrize(initCustomPrize());
            setPreviewImage('');
            setShowCustomForm(true);
          }}
          className="w-full md:w-auto px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-bold text-lg hover:shadow-2xl transform hover:scale-105 transition-all"
        >
          <Plus className="inline-block mr-2" />
          オリジナル景品を作成
        </button>
      </div>

      {/* 一覧ジャンプ */}
      <div className="max-w-6xl mx-auto mb-4">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => scrollToSection('prize-section')}
            className="px-3 py-2 text-sm bg-white border border-purple-300 text-purple-700 rounded-lg hover:bg-purple-50 transition-all"
          >
            🎁 景品交換へ移動
          </button>
          <button
            onClick={() => scrollToSection('coupon-section')}
            className="px-3 py-2 text-sm bg-white border border-pink-300 text-pink-700 rounded-lg hover:bg-pink-50 transition-all"
          >
            🎟️ クーポン交換へ移動
          </button>
        </div>
      </div>

      {/* 現在の景品一覧 */}
      <div className="max-w-6xl mx-auto mb-6">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-6 text-center">📋 現在の景品（{prizes.length}個）</h2>

          {prizes.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <div className="text-6xl mb-4">📭</div>
              <p className="text-xl">まだ景品がありません</p>
            </div>
          ) : (
            <div className="space-y-8">
              <div id="prize-section">
                <h3 className="text-xl font-bold mb-4">🎁 景品交換（{prizeItems.length}件）</h3>
                {prizeItems.length === 0 ? (
                  <div className="bg-gray-50 rounded-lg p-4 text-gray-500 text-sm">
                    該当する景品はありません
                  </div>
                ) : (
                  renderPrizeCards(prizeItems)
                )}
              </div>

              <div id="coupon-section">
                <h3 className="text-xl font-bold mb-4">🎟️ クーポン交換（{couponItems.length}件）</h3>
                {couponItems.length === 0 ? (
                  <div className="bg-gray-50 rounded-lg p-4 text-gray-500 text-sm">
                    該当するクーポンはありません
                  </div>
                ) : (
                  renderPrizeCards(couponItems)
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* カスタム景品作成フォーム */}
      {showCustomForm && editingPrize && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">
                {editingPrize.id ? '景品を編集' : 'オリジナル景品を作成'}
              </h2>
              <button
                onClick={() => {
                  setShowCustomForm(false);
                  setEditingPrize(null);
                  setPreviewImage('');
                }}
                className="w-10 h-10 bg-gray-200 rounded-full hover:bg-gray-300"
              >
                <X className="w-6 h-6 mx-auto" />
              </button>
            </div>

            <div className="space-y-6">
              {/* 画像アップロード（スマホ対応） */}
              <div>
                <label className="block text-sm font-bold mb-2">画像（任意）</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  {previewImage ? (
                    <div className="relative">
                      <img src={previewImage} alt="プレビュー" className="w-full h-48 object-cover rounded-lg" />
                      <button
                        onClick={() => {
                          setPreviewImage('');
                          setEditingPrize({ ...editingPrize, image: null });
                        }}
                        className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <>
                      <Camera className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={handleImageSelect}
                        className="hidden"
                      />
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="px-6 py-3 bg-blue-500 text-white rounded-lg font-bold hover:bg-blue-600"
                      >
                        <Camera className="inline-block mr-2" />
                        写真を撮る / 選択
                      </button>
                      <p className="text-gray-500 text-sm mt-2">
                        スマホなら直接カメラが起動します
                      </p>
                    </>
                  )}
                </div>
              </div>

              {/* アイコン選択 */}
              <div>
                <label className="block text-sm font-bold mb-2">アイコン</label>
                <div className="space-y-4">
                  {iconCategories.map((category, idx) => (
                    <div key={idx}>
                      <div className="text-sm text-gray-600 mb-2">{category.name}</div>
                      <div className="grid grid-cols-6 md:grid-cols-12 gap-1">
                        {category.icons.map(icon => (
                          <button
                            key={icon}
                            onClick={() => setEditingPrize({ ...editingPrize, icon })}
                            className={`p-2 text-2xl rounded-lg border-2 transition-all ${
                              editingPrize.icon === icon
                                ? 'border-purple-500 bg-purple-50'
                                : 'border-gray-200 hover:border-gray-400'
                            }`}
                          >
                            {icon}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 景品名 */}
              <div>
                <label className="block text-sm font-bold mb-2">種別</label>
                <select
                  value={editingPrize.category || 'custom'}
                  onChange={(e) => setEditingPrize({ ...editingPrize, category: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg"
                >
                  <option value="custom">景品交換</option>
                  <option value="coupon">クーポン交換</option>
                </select>
              </div>

              {/* 値引きクーポン設定 */}
              {editingPrize.category === 'coupon' && (
                <div className="space-y-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div>
                    <label className="block text-sm font-bold mb-2">値引き種別</label>
                    <select
                      value={editingPrize.discount_type || 'fixed'}
                      onChange={(e) => setEditingPrize({ ...editingPrize, discount_type: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg"
                    >
                      <option value="fixed">固定額値引き</option>
                      <option value="percentage" disabled>割合値引き（%）</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-bold mb-2">値引き値</label>
                    <input
                      type="number"
                      value={editingPrize.discount_value ?? 0}
                      onChange={(e) => setEditingPrize({ ...editingPrize, discount_value: Number(e.target.value) })}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg"
                      min={1}
                    />
                  </div>
                </div>
              )}

              {/* 景品名 */}
              <div>
                <label className="block text-sm font-bold mb-2">景品名（必須）</label>
                <input
                  type="text"
                  value={editingPrize.name}
                  onChange={(e) => setEditingPrize({ ...editingPrize, name: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-lg"
                  placeholder="例: 特製ハンバーグ弁当無料券"
                />
              </div>

              {/* 説明 */}
              <div>
                <label className="block text-sm font-bold mb-2">説明（任意）</label>
                <textarea
                  value={editingPrize.desc}
                  onChange={(e) => setEditingPrize({ ...editingPrize, desc: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg"
                  rows={3}
                  placeholder="例: 当店自慢のハンバーグ弁当が無料になります"
                />
              </div>

              {/* ポイント数 */}
              <div>
                <label className="block text-sm font-bold mb-2">必要ポイント（必須）</label>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setEditingPrize({
                      ...editingPrize,
                      points: Math.max(10, editingPrize.points - 10)
                    })}
                    className="w-12 h-12 bg-red-500 text-white rounded-full text-xl font-bold"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    value={editingPrize.points}
                    onChange={(e) => setEditingPrize({ ...editingPrize, points: Number(e.target.value) })}
                    className="w-32 text-2xl font-bold text-center border-2 border-gray-300 rounded-lg"
                  />
                  <span className="text-lg">pt</span>
                  <button
                    onClick={() => setEditingPrize({
                      ...editingPrize,
                      points: editingPrize.points + 10
                    })}
                    className="w-12 h-12 bg-blue-500 text-white rounded-full text-xl font-bold"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>

            {/* 保存ボタン */}
            <div className="flex space-x-4 mt-8">
              <button
                onClick={() => {
                  setShowCustomForm(false);
                  setEditingPrize(null);
                  setPreviewImage('');
                }}
                className="flex-1 py-3 bg-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-400"
              >
                キャンセル
              </button>
              <button
                onClick={addCustomPrize}
                className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-bold hover:shadow-lg"
              >
                <Save className="inline-block mr-2" />
                {editingPrize.id ? '更新する' : '追加する'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 保存ボタン */}
      <div className="max-w-6xl mx-auto">
        <button
          onClick={saveAll}
          className="w-full py-6 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-bold text-2xl hover:shadow-2xl transform hover:scale-105 transition-all"
        >
          <Save className="inline-block mr-3" size={32} />
          設定を保存する
        </button>
      </div>
    </div>
  );
};

export default PointShopManagerCustom;
